import {
  useEffect,
} from "react";

import {
  useTranslation,
} from "react-i18next";

import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";

import routeConfig, {
  getLanguageFromPath,
  getRoutePath,
} from "./site/config/routes";

import useTheme from "./template/hooks/useTheme";

import Navbar from "./template/components/layout/Navbar";
import ScrollToTopButton from "./template/components/common/ScrollToTopButton";
import ScrollToTopOnRouteChange from "./template/components/common/ScrollToTopOnRouteChange";

import HomePage from "./pages/HomePage";
import PortfolioPage from "./pages/PortfolioPage";
import SessionsPage from "./pages/SessionsPage";
import StudioPage from "./pages/StudioPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import PricingPage from "./pages/PricingPage";

import PremiumIntroModal from "./template/components/common/PremiumIntroModal";

/*
  ============================================================
  FOUNDATION PAGE
  ============================================================

  Muut sivut ovat vielä väliaikaisia foundation-näkymiä.
*/

function FoundationPage({
  pageId,
}) {
  const {
    t,
  } = useTranslation("common");

  const location =
    useLocation();

  const language =
    getLanguageFromPath(
      location.pathname
    );

  return (
    <div
      className="
        mx-auto
        flex
        min-h-[calc(100svh-90px)]
        w-full
        max-w-editorial
        flex-col
        px-6
        sm:px-10
        lg:px-16
      "
    >
      <div
        className="
          flex
          flex-1
          items-center
          py-20
          lg:py-28
        "
      >
        <div className="max-w-5xl">
          <p
            className="
              mb-8
              flex
              flex-wrap
              items-center
              gap-3
              text-xs
              uppercase
              tracking-[0.32em]
              text-muted
            "
          >
            <span>
              {t(
                "demo.eyebrow"
              )}
            </span>

            <span
              aria-hidden="true"
            >
              /
            </span>

            <span>
              {t(
                `pages.${pageId}`
              )}
            </span>
          </p>

          <h1
            className="
              max-w-4xl
              font-display
              text-5xl
              font-normal
              leading-[0.95]
              tracking-[-0.035em]
              sm:text-7xl
              lg:text-8xl
            "
          >
            {t(
              "demo.title"
            )}
          </h1>

          <p
            className="
              mt-10
              max-w-reading
              text-base
              leading-8
              text-muted
              sm:text-lg
            "
          >
            {t(
              "demo.description"
            )}
          </p>

          <div
            className="
              mt-12
              flex
              flex-wrap
              gap-5
            "
          >
            <Link
              to={getRoutePath(
                "portfolio",
                language
              )}
              className="
                bg-text
                px-7
                py-4
                text-xs
                uppercase
                tracking-[0.18em]
                text-canvas
                transition-opacity
                hover:opacity-80
              "
            >
              {t(
                "demo.portfolio"
              )}
            </Link>

            <Link
              to={getRoutePath(
                "services",
                language
              )}
              className="
                border
                border-border
                px-7
                py-4
                text-xs
                uppercase
                tracking-[0.18em]
                transition
                hover:border-text
              "
            >
              {t(
                "demo.services"
              )}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

/*
  ============================================================
  404
  ============================================================
*/

function NotFoundPage() {
  const {
    t,
  } = useTranslation("common");

  const location =
    useLocation();

  const language =
    getLanguageFromPath(
      location.pathname
    );

  return (
    <div
      className="
        mx-auto
        flex
        min-h-[calc(100svh-90px)]
        w-full
        max-w-editorial
        items-center
        px-6
        py-20
        sm:px-10
        lg:px-16
        lg:py-28
      "
    >
      <div className="max-w-3xl">
        <p
          className="
            text-xs
            uppercase
            tracking-[0.32em]
            text-muted
          "
        >
          {t(
            "notFound.code"
          )}
        </p>

        <h1
          className="
            mt-8
            font-display
            text-5xl
            leading-none
            tracking-[-0.035em]
            sm:text-7xl
          "
        >
          {t(
            "notFound.title"
          )}
        </h1>

        <p
          className="
            mt-8
            max-w-reading
            text-base
            leading-8
            text-muted
            sm:text-lg
          "
        >
          {t(
            "notFound.description"
          )}
        </p>

        <Link
          to={getRoutePath(
            "home",
            language
          )}
          className="
            mt-10
            inline-flex
            border
            border-border
            px-7
            py-4
            text-xs
            uppercase
            tracking-[0.18em]
            transition
            hover:border-text
          "
        >
          {t(
            "notFound.backHome"
          )}
        </Link>
      </div>
    </div>
  );
}

/*
  ============================================================
  APP
  ============================================================

  App vastaa nyt vain:

  - globaalista teemasta
  - URL:n ja i18nextin synkronoinnista
  - reitityksestä

  Navbarin oma UI-logiikka on Navbar.jsx:ssä.
*/

export default function App() {
  const {
    i18n,
  } = useTranslation(
    "common"
  );

  const location =
    useLocation();

  const {
    theme,
    themes,
    setTheme,
  } = useTheme();

  /*
    URL on sivuston kielen ensisijainen lähde.
  */
  const routeLanguage =
    getLanguageFromPath(
      location.pathname
    );

  /*
    Synkronoidaan i18next aina URL:n kanssa.
  */
  useEffect(() => {
    const currentLanguage =
      String(
        i18n.resolvedLanguage ||
        i18n.language ||
        ""
      )
        .trim()
        .toLowerCase()
        .split("-")[0];

    if (
      currentLanguage !==
      routeLanguage
    ) {
      i18n.changeLanguage(
        routeLanguage
      );
    }

    document.documentElement.lang =
      routeLanguage;

    document.documentElement.dir =
      "ltr";
  }, [
    i18n,
    routeLanguage,
  ]);

  return (
    <main
      className="
        min-h-screen
        bg-canvas
        text-text
        transition-colors
        duration-500
      "
    >
      {/* ======================================================
          GLOBAL NAVIGATION
          ====================================================== */}

      <Navbar
        theme={theme}
        themes={themes}
        setTheme={setTheme}
      />

      <PremiumIntroModal
        language={routeLanguage}
      />

      {/* ======================================================
          ROUTES
          ====================================================== */}

      <ScrollToTopOnRouteChange />

      <Routes>
        <Route
          path={
            routeConfig.home
              .paths.fi
          }
          element={
            <HomePage
              theme={theme}
            />
          }
        />

        <Route
          path="/en"
          element={
            <Navigate
              to="/en/"
              replace
            />
          }
        />

        <Route
          path={
            routeConfig.home
              .paths.en
          }
          element={
            <HomePage
              theme={theme}
            />
          }
        />

        <Route
          path={
            routeConfig.portfolio
              .paths.fi
          }
          element={
            <PortfolioPage />
          }
        />

        <Route
          path={
            routeConfig.portfolio
              .paths.en
          }
          element={
            <PortfolioPage />
          }
        />

        <Route
          path={
            routeConfig.services
              .paths.fi
          }
          element={
            <SessionsPage />
          }
        />

        <Route
          path={
            routeConfig.services
              .paths.en
          }
          element={
            <SessionsPage />
          }
        />

        <Route
          path={
            routeConfig.studio
              .paths.fi
          }
          element={
            <StudioPage />
          }
        />

        <Route
          path={
            routeConfig.studio
              .paths.en
          }
          element={
            <StudioPage />
          }
        />

        <Route
          path={
            routeConfig.pricing
              .paths.fi
          }
          element={
            <PricingPage />
          }
        />

        <Route
          path={
            routeConfig.pricing
              .paths.en
          }
          element={
            <PricingPage />
          }
        />

        <Route
          path={
            routeConfig.about
              .paths.fi
          }
          element={
            <AboutPage />
          }
        />

        <Route
          path={
            routeConfig.about
              .paths.en
          }
          element={
            <AboutPage />
          }
        />

        <Route
          path={
            routeConfig.contact
              .paths.fi
          }
          element={
            <ContactPage
              theme={theme}
            />
          }
        />

        <Route
          path={
            routeConfig.contact
              .paths.en
          }
          element={
            <ContactPage
              theme={theme}
            />
          }
        />

        <Route
          path="*"
          element={
            <NotFoundPage />
          }
        />
      </Routes>

      <ScrollToTopButton
        language={routeLanguage}
      />
    </main>
  );
}